import React, { useState } from 'react';
import { FadeIn, CTAButton, Section } from './Shared';
import { LocalCurrencyNote } from './LocalCurrencyNote';
import { ResultsCarousel } from './ResultsCarousel';
import { useCountdown } from '../hooks/useCountdown';
import { markCheckoutStarted } from '../hooks/useExitIntent';
import { CheckCircle2, AlertTriangle, BookOpen, Plus, Minus } from 'lucide-react';
import { PRODUCTS } from './MetaPixel';
export const ProductTomos = () => {
  const [openManual, setOpenManual] = useState<number | null>(null);
  const phases = [{
    name: 'Fase 1 — Los Cimientos',
    modules: [{
      n: 1,
      title: 'Fundamentos de la Colorimetría',
      time: '~40 min'
    }, {
      n: 2,
      title: 'Decoloración Uniforme',
      time: '~35 min'
    }, {
      n: 3,
      title: 'Neutralizando Tonos Amarillos',
      time: '~30 min'
    }]
  }, {
    name: 'Fase 2 — Dominio de Platinados',
    modules: [{
      n: 4,
      title: 'Global en Platinado',
      time: '~45 min'
    }, {
      n: 5,
      title: 'Global Blanco',
      time: '~50 min'
    }, {
      n: 6,
      title: 'Gris Claro Paso a Paso',
      time: '~40 min'
    }]
  }, {
    name: 'Fase 3 — Técnicas de Diseño',
    modules: [{
      n: 7,
      title: 'Mechas Platinadas',
      time: '~38 min'
    }, {
      n: 8,
      title: 'Mechas Primavera/Verano',
      time: '~35 min'
    }, {
      n: 9,
      title: 'Global + Cúspide',
      time: '~42 min'
    }, {
      n: 10,
      title: 'Franjas Paso a Paso',
      time: '~40 min'
    }, {
      n: 11,
      title: 'Fórmulas y Colores Fantasías',
      time: '~45 min'
    }]
  }, {
    name: 'Fase 4 — Técnicas Avanzadas Roots',
    modules: [{
      n: 12,
      title: 'Técnica Roots Paso a Paso',
      time: '~50 min'
    }, {
      n: 13,
      title: 'Platinado con Técnica Root',
      time: '~48 min'
    }, {
      n: 14,
      title: 'Técnicas Roots Brasileñas',
      time: '~45 min'
    }, {
      n: 15,
      title: 'Peinados Modernos',
      time: '~35 min'
    }]
  }];
  const manuals = [{
    emoji: '🟡',
    title: 'Manual 1 — Fundamentos Pro',
    pages: '~120 páginas',
    extra: '+ Tablas de neutralización',
    desc: 'La base que casi nadie domina bien: niveles y alturas de tono, fondos de aclaración, subtonos cálidos y la rueda del color aplicada al cabello masculino. Incluye las tablas de neutralización para saber, sin adivinar, qué matiz apaga cada reflejo amarillo o naranja.',
    borderColor: 'border-t-[#EAB308]'
  }, {
    emoji: '🟠',
    title: 'Manual 2 — Análisis y Paletas Masculinas',
    pages: '~110 páginas',
    extra: '+ Fichas de diagnóstico visual',
    desc: 'Cómo leer al cliente antes de tocar el cabello: tono de piel, ojos, porosidad, canas, historial químico y estado del cabello. Con las 12 paletas masculinas y las fichas de diagnóstico para elegir el color que realmente le queda y venderlo con seguridad.',
    borderColor: 'border-t-[#EA580C]'
  }, {
    emoji: '🔴',
    title: 'Manual 3 — De la Teoría a la Práctica',
    pages: '~125 páginas',
    extra: '+ Fórmulas gramo a gramo exactas',
    desc: 'El manual de la silla: fórmulas gramo a gramo, volumen de oxidante correcto, tiempos de exposición, decoloración por secciones y protocolos para corregir errores sin quemar el cabello. Lo abres, copias la fórmula y trabajas.',
    borderColor: 'border-t-[#DC2626]'
  }, {
    emoji: '🟣',
    title: 'Manual 4 — Técnicas y Diseños de Colorimetría',
    pages: '~120 páginas',
    extra: '+ Biblioteca de diseños',
    desc: 'La biblioteca completa de diseños: platinados y blancos, grises, roots, mechas, franjas, cúspides y fantasías. Cada técnica con su esquema de aplicación paso a paso y las combinaciones listas para mostrarle al cliente antes de empezar.',
    borderColor: 'border-t-[#8B5CF6]'
  }];
  const features = ["El paso a paso para dominar la técnica", "Teoría y práctica con fundamentos profesionales", "Tendencias actuales con su explicación", "Productos y fórmulas exactas  que debes utilizar", "Material de referencia — para que utilizes como guía", 'Herramientas complementarias ya integradas', "Un sistema organizado — Nada disperso"];
  const featureChips = ['✅ Domina el sistema de colorimetría masculina completo', '✅ TODO conectado y en un solo lugar', '✅ Técnicas probadas listas para aplicar', '✅ Fórmulas exactas gramo a gramo', '✅ 475+ páginas + 15 horas de video'];
  return <Section className="bg-bg-base !pt-3 md:!pt-8" data-id="element-19" data-mp="1vm5o3kfaacfm">
      <FadeIn className="text-center mb-8 md:mb-12" data-id="element-20" data-mp="1vngt1nkgtq8b">
        {/* Gold trust pill */}
        <div className="flex justify-center mb-4 md:mb-5" data-id="element-21" data-mp="20mbgx3xai4za">
          <span className="inline-flex items-center gap-1.5 font-sans font-bold uppercase tracking-[1px] text-[9px] md:text-[11px] px-3 py-1 md:px-3.5 md:py-1.5 rounded-full bg-brandGold text-[#14100A]" style={{
          boxShadow: '0 0 20px rgba(212,162,76,0.35)'
        }} data-id="element-22" data-mp="1mrrbxqq1vi0q">
            🏆 El Sistema Completo de Colorimetría Masculina
          </span>
        </div>

        <h2 className="font-display font-bold uppercase text-txt-primary mb-3 md:mb-4 md:text-[36px] text-[22px] leading-[1.25] tracking-[0.5px]" data-id="element-23" data-mp="7vga5escijbr">
          🎨 MEGAPACK COLORIMETRÍA{' '}
          <span className="text-brandGold" data-id="element-24" data-mp="hlj9dwq28tij">LISTA™</span>
        </h2>
        <p className="font-sans text-[14px] md:text-[16px] leading-[1.5] md:leading-[1.7] text-txt-body" data-id="element-25" data-mp="hkrdslwgqvs9">
          15 Módulos en Video + 4 Manuales Teóricos + 5 Bonos Gratis · Curso Completo - Listo para aplicar desde el primer día
        </p>
      </FadeIn>

      {/* Product mockup */}
      <FadeIn delay={0.2} className="mb-5 md:mb-8" data-id="element-26" data-mp="3g4pyf3xk8fol">
        <img src="https://cdn.magicpatterns.com/uploads/5BcCuGo5xVLsNtw3DLTxzP/ChatGPT_Image_24_ago_2026,_11_37_14.png" alt="Megapack Colorimetría Lista: 15 módulos en video, 4 manuales y 5 bonos" loading="lazy" decoding="async" width={900} height={900} className="mx-auto block w-full max-w-[340px] md:max-w-[620px] h-auto object-contain drop-shadow-2xl" data-id="element-27" data-mp="3v05sh8y54bkz" />
        <div className="text-center mt-3 md:mt-4" data-id="element-28" data-mp="15au16opfndkj">
          <span className="inline-block bg-brandGold text-[#14100A] font-sans font-bold uppercase tracking-[1.5px] text-[9.5px] md:text-[11px] px-3 py-1 md:px-4 md:py-1.5 rounded-full" data-id="element-29" data-mp="1cf3cfqxh66bg">
            ✨ CURSO DIGITAL COMPLETO
          </span>
        </div>
      </FadeIn>

      <FadeIn delay={0.3} className="mb-8 md:mb-12" data-id="element-30" data-mp="1rbkneh1r5ixh">
        <div className="flex flex-wrap justify-center gap-x-1 gap-y-1 md:gap-x-1.5 md:gap-y-1.5 max-w-3xl mx-auto" data-id="element-31" data-mp="1ra9lynvs4n9j">
          {featureChips.map((chip, i) => <span key={i} className="bg-brandGold/10 text-brandGold border border-brandGold/30 px-1.5 py-0.5 md:px-2 md:py-1 rounded-full font-sans font-bold uppercase tracking-[0.5px] md:tracking-[1px] text-[8.5px] md:text-[10px] whitespace-nowrap" data-id="element-32" data-mp="ymwy8advu9en" data-mpi={i}>
              {chip}
            </span>)}
        </div>
      </FadeIn>

      <FadeIn delay={0.35} className="text-center mb-5 md:mb-7" data-id="element-33" data-mp="axujbaamuunu">
        <div className="inline-flex items-center gap-2 bg-bg-card border border-brandGold/30 rounded-full px-4 py-1.5 md:px-5 md:py-2" data-id="element-34" data-mp="avi24zcp8obu">
          <span className="font-sans font-bold uppercase tracking-[1.5px] text-[11px] md:text-[13px] text-brandGold" data-id="element-35" data-mp="2t773dquycxqk">
            📦 Lo que recibes inmediatamente:
          </span>
        </div>
      </FadeIn>

      {/* Video curriculum */}
      <FadeIn delay={0.4} className="mb-10 md:mb-14" data-id="element-36" data-mp="2k460dz1dvmr4">
        <h3 className="font-display font-bold uppercase text-[18px] md:text-[26px] leading-[1.2] tracking-[0.5px] text-txt-primary text-center mb-5 md:mb-8" data-id="element-37" data-mp="2f7fm9h95318c">
          🎬 Entrenamiento completo en videos{' '}
          <span className="text-brandGold" data-id="element-38" data-mp="25gun9spjzuow">(15 Módulos)</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5 max-w-4xl mx-auto" data-id="element-39" data-mp="21f9h0q7iyeno">
          {phases.map(phase => <div key={phase.name} className="bg-bg-card rounded-[16px] border border-line p-4 md:p-5" data-id="element-40" data-mp="1wgy5mb1kzw3c">
              <p className="font-display font-bold uppercase tracking-[1px] text-[12px] md:text-[14px] text-brandGold mb-3" data-id="element-41" data-mp="1rhla71wn9gp5">
                {phase.name}
              </p>
              <ul className="divide-y divide-line" data-id="element-42" data-mp="3bcfz69nysh1q">
                {phase.modules.map(mod => <li key={mod.n} className="flex items-center gap-2.5 py-2" data-id="element-43" data-mp="3babl4ywcq1nq">
                    <span className="font-display font-bold text-[13px] md:text-[15px] text-txt-faint w-6 shrink-0" data-id="element-44" data-mp="3fdhre6jgrsu7">
                      {mod.n.toString().padStart(2, '0')}
                    </span>
                    <span className="font-sans text-[12.5px] md:text-[14px] leading-snug text-txt-body flex-1 min-w-0" data-id="element-45" data-mp="36brzkzlo6rvw">
                      {mod.title}
                    </span>
                    <span className="font-sans font-bold text-[10px] md:text-[11px] text-txt-muted shrink-0" data-id="element-46" data-mp="2wg70b97zk5pu">
                      {mod.time}
                    </span>
                  </li>)}
              </ul>
            </div>)}
        </div>
      </FadeIn>

      {/* Manuals */}
      <FadeIn delay={0.45} className="mb-10 md:mb-14" data-id="element-47" data-mp="28p2swcg0vr3p">
        <h3 className="font-display font-bold uppercase text-[18px] md:text-[26px] leading-[1.2] tracking-[0.5px] text-txt-primary text-center mb-5 md:mb-8" data-id="element-48" data-mp="28rntyz8hy30e">
          📘 Manuales Técnicos{' '}
          <span className="text-brandGold" data-id="element-49" data-mp="1u4yli7w9ofhv">(4 Tomos Completos — +475 páginas)</span>
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-5 max-w-4xl mx-auto" data-id="element-50" data-mp="1q33kl4i9044m">
          {manuals.map((manual, i) => {
          const isOpen = openManual === i;
          return <button key={manual.title} type="button" onClick={() => setOpenManual(isOpen ? null : i)} aria-expanded={isOpen} className={`w-full text-left bg-bg-card rounded-[16px] md:rounded-[20px] p-4 md:p-6 border-x border-b border-t-[3px] transition-[border-color,box-shadow] duration-200 ${isOpen ? 'border-x-brandGold border-b-brandGold shadow-[0_0_40px_rgba(212,162,76,0.18)]' : 'border-x-line border-b-line'} ${manual.borderColor}`} data-id="element-51" data-mp="1z0dx0t6sxzb4" data-mpi={i}>
              <div className="flex items-start justify-between gap-3" data-id="element-52" data-mp="2wpqr3jfncvjq">
                <div className="flex items-start gap-2.5 md:gap-3 min-w-0 flex-1" data-id="element-53" data-mp="2wpz3la7uxyue">
                  <div className="text-2xl md:text-3xl shrink-0" aria-hidden="true" data-id="element-54" data-mp="2rs7e0muoqotd">
                    {manual.emoji}
                  </div>
                  <div className="min-w-0" data-id="element-55" data-mp="2m6smr9iqjbok">
                    <h4 className="font-sans font-bold text-[14px] md:text-[17px] text-txt-primary leading-snug mb-1.5 md:mb-2" data-id="element-56" data-mp="2m60pzvdej7wp">
                      {manual.title}
                    </h4>
                    <div className="flex flex-wrap gap-1.5" data-id="element-57" data-mp="2d75hgcmvxlq8">
                      <span className="font-sans font-bold uppercase tracking-[1px] text-[9px] md:text-[11px] text-brandGold bg-brandGold/10 px-2 py-0.5 rounded" data-id="element-58" data-mp="39weh9jo5x2q8">
                        {manual.pages}
                      </span>
                      <span className="font-sans font-bold uppercase tracking-[1px] text-[9px] md:text-[11px] text-txt-muted bg-white/5 px-2 py-0.5 rounded" data-id="element-59" data-mp="30ux4aa61xh5r">
                        {manual.extra}
                      </span>
                    </div>
                  </div>
                </div>
                <div className={`w-7 h-7 md:w-8 md:h-8 rounded-full flex items-center justify-center shrink-0 transition-colors duration-200 ${isOpen ? 'bg-brandGold text-[#14100A]' : 'bg-bg-highlight text-txt-primary'}`} data-id="element-60" data-mp="1kh6zf173yov2">
                  {isOpen ? <Minus size={14} data-id="element-61" data-mp="4yqxekfl6a18" /> : <Plus size={14} data-id="element-62" data-mp="4w4puksp3uj0" />}
                </div>
              </div>
              <div className={`grid transition-[grid-template-rows,opacity,margin] duration-300 ease-out ${isOpen ? 'grid-rows-[1fr] opacity-100 mt-3 md:mt-4' : 'grid-rows-[0fr] opacity-0'}`} data-id="element-63" data-mp="8yt10z4vtdj5">
                <div className="overflow-hidden" data-id="element-64" data-mp="ipdvcadivvw9">
                  <p className="font-sans text-[13.5px] md:text-[15px] leading-[1.6] text-txt-body" data-id="element-65" data-mp="noqjcyg4r90f">
                    {manual.desc}
                  </p>
                </div>
              </div>
            </button>;
        })}
        </div>
      </FadeIn>

      <FadeIn delay={0.5} data-id="element-66" data-mp="wovot31rjxby">
        <div className="bg-bg-card rounded-[20px] p-5 md:p-10 border border-line max-w-4xl mx-auto" data-id="element-67" data-mp="2ufg8e0hl49vd">
          <h3 className="font-sans font-bold text-lg md:text-xl text-center text-txt-primary leading-snug mb-5 md:mb-8 flex items-center justify-center gap-2" data-id="element-68" data-mp="2yh1u8bc3jyhp">
            <BookOpen size={20} className="text-brandGold" aria-hidden="true" data-id="element-69" data-mp="5n35ei9dwlz9" />
            Cada módulo incluye:
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2.5 md:gap-4" data-id="element-70" data-mp="ptbapb1vm53">
            {features.map((feature, i) => <div key={i} className="flex items-start gap-2 md:gap-3" data-id="element-71" data-mp="alw95iyvcym8" data-mpi={i}>
                <CheckCircle2 className="text-success shrink-0 mt-0.5 w-4 h-4 md:w-5 md:h-5" data-id="element-72" data-mp="anquuzhysurd" />
                <span className="font-sans text-[14px] md:text-[16px] leading-[1.6] text-txt-body" data-id="element-73" data-mp="ojcgqxswiyfk">
                  {feature}
                </span>
              </div>)}
          </div>

          <div className="mt-6 md:mt-8 text-center" data-id="element-74" data-mp="yafim1tnnhfw">
            <p className="font-sans font-bold text-base md:text-xl text-brandGold leading-snug" data-id="element-75" data-mp="tbm72exxi0id">
              Material listo y organizado para que te conviertas en un experto de la COLORIMETRÍA. Tu cliente queda perfecto y vos cobras el triple. Así de simple.
            </p>
          </div>
        </div>
      </FadeIn>

      {/* What you'll achieve */}
      <FadeIn delay={0.15} className="mt-12 md:mt-20" data-id="element-76" data-mp="1lwj5gu8s1p2k">
        <div className="text-center mb-5 md:mb-8" data-id="element-77" data-mp="1qxypciz7xpjd">
          <p className="font-sans font-bold uppercase tracking-[1.8px] text-[10px] md:text-[11px] text-brandGold mb-2" data-id="element-78" data-mp="1qvcm2vjd1w6y">
            Resultados reales en la silla
          </p>
          <h3 className="font-display font-bold uppercase text-[24px] md:text-[40px] leading-[1.15] tracking-[0.5px] text-txt-primary" data-id="element-79" data-mp="1gxy7l1gzzhha">
            LO QUE VAS A <span className="text-brandGold" data-id="element-80" data-mp="17zcq79n9i8i3">LOGRAR</span>
          </h3>
          <p className="font-sans text-[13px] md:text-[16px] leading-[1.6] text-txt-body max-w-[560px] mx-auto mt-2.5" data-id="element-81" data-mp="180nurboj7ka9">
            Platinados limpios, grises parejos y fantasías sin quemar el
            cabello. Este es el nivel de trabajo que vas a poder cobrar al
            triple.
          </p>
        </div>

        <ResultsCarousel data-id="element-82" data-mp="p83fwy5o3fpg" />

        <div className="flex flex-wrap justify-center gap-2 mt-5 md:mt-6" data-id="element-83" data-mp="p8mq1sp2szsx">
          {['Platinados y blancos puros', 'Grises y plateados parejos', 'Fantasías sin arruinar el cabello', 'Roots y diseños de barbería'].map(item => <span key={item} className="inline-flex items-center gap-1.5 bg-bg-card border border-line rounded-full px-3 py-1.5 font-sans text-[11px] md:text-[13px] text-txt-body" data-id="element-84" data-mp="c9vor3qw6io7">
              <CheckCircle2 size={13} className="text-success shrink-0" aria-hidden="true" data-id="element-85" data-mp="3f7hj81raqwr" />
              {item}
            </span>)}
        </div>
      </FadeIn>
    </Section>;
};
export const Bonuses = () => {
  const bonuses = [{
    num: '01',
    icon: '🎨',
    title: 'Cartas de Color Masculinas (12 Paletas)',
    desc: 'Una para cada tipo de cliente. Imprimibles. Listas para usar como herramienta de venta en la silla.',
    value: 'USD $8',
    image: 'https://cdn.magicpatterns.com/uploads/jp4BHjGt1Ao6b3QQtsZsur/bono_1.webp'
  }, {
    num: '02',
    icon: '🇦🇷',
    title: 'Kit de Abastecimiento de Insumos (Marcas Reales)',
    desc: 'Nov, Issue, Fidelité, Schwarzkopf... Exactamente qué comprar, dónde y cuánto cuesta. La guía que te ahorra años de error.',
    value: 'USD $7',
    image: 'https://cdn.magicpatterns.com/uploads/1tHXueVXXLQSELBTV82b69/bono_2.webp'
  }, {
    num: '03',
    icon: '📋',
    title: 'Fichas Profesionales de Diagnóstico',
    desc: 'Registra cada cliente, diagnóstico, fórmula y resultado. Tenlo impreso. Tu sistema administrativo en papel.',
    value: 'USD $7',
    image: 'https://cdn.magicpatterns.com/uploads/rEXxahmnd5StBNzD3eCcrc/bono_3.webp'
  }, {
    num: '04',
    icon: '🎯',
    title: 'Guía de Combinaciones Visuales',
    desc: '100+ combinaciones de color para mostrar al cliente. Inspírate o copia. Listo para usar.',
    value: 'USD $8',
    image: 'https://cdn.magicpatterns.com/uploads/kdfga7K1WNEQoSC3MQapUH/Bono_4_nuevo.webp'
  }, {
    num: '05',
    icon: '🔥',
    title: 'Kit Profesional del Asesor de Color',
    desc: 'Checklists de seguridad, protocolos de emergencia, agenda de sesiones, registro de trabajos. TODO lo que necesitas para profesionalizarte.',
    value: 'USD $9',
    image: 'https://cdn.magicpatterns.com/uploads/5NkpdiA9ccM9zaZN6Q2nt4/bono_5.webp'
  }];
  return <Section className="bg-bg-alt" data-id="element-86" data-mp="1z7mdan0hdecv">
      <FadeIn className="text-center mb-8 md:mb-14" data-id="element-87" data-mp="2d5azby6aarji">
        <h2 className="font-display font-bold uppercase md:text-[40px] text-txt-primary mb-2 md:mb-4 text-[26px] tracking-[0.5px]" data-id="element-88" data-mp="2d2yi5n8col7i">
          + 5 BONOS <span className="text-brandGold" data-id="element-89" data-mp="1kfu6x0inzana">EXCLUSIVOS</span>
        </h2>
        <p className="font-sans text-[14px] md:text-[16px] leading-[1.5] md:leading-[1.7] text-txt-body" data-id="element-90" data-mp="1fhanp7vggcym">
          Materiales complementarios — sin costo extra
        </p>
      </FadeIn>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-4 mb-8 md:mb-12" data-id="element-91" data-mp="1u8018gwe245n">
        {bonuses.map((bono, i) => <FadeIn key={bono.num} delay={Math.min(i * 0.05, 0.2)} data-id="element-92" data-mp="11pw1yta5xxnv" data-mpi={i}>
            <article className="bg-bg-card rounded-[16px] border border-line border-l-[3px] border-l-brandGold h-full overflow-hidden flex gap-3 p-3 md:gap-4 md:p-4" data-id="element-93" data-mp="11o1hth6pu9ku">
              <img src={bono.image} alt={`Portada del bono ${bono.num}: ${bono.title}`} loading="lazy" decoding="async" width={128} height={171} className="w-24 self-start aspect-[3/4] shrink-0 rounded-[10px] object-cover border border-white/10 md:w-32" data-id="element-94" data-mp="38d3hs95nz71p" />
              <div className="min-w-0 flex flex-1 flex-col" data-id="element-95" data-mp="2zbn8y5uhzczl">
                <div className="flex items-center justify-between gap-2 mb-1.5" data-id="element-96" data-mp="33fl5c8sfu9oy">
                  <div className="flex items-center gap-1.5" data-id="element-97" data-mp="3r5paikl9yhi2">
                    <span className="text-lg md:text-xl" data-id="element-98" data-mp="3w3qud7qiykul">🎁</span>
                    <span className="font-sans font-bold uppercase tracking-[1px] text-[9px] md:text-[10px] text-brandGold" data-id="element-99" data-mp="3w2xrwiylmhi9">
                      BONO {bono.num}
                    </span>
                  </div>
                  <span className="text-lg md:text-xl" aria-hidden="true" data-id="element-100" data-mp="3n55934to4rq4">{bono.icon}</span>
                </div>
                <h3 className="font-sans font-bold text-[13.5px] md:text-[15px] text-txt-primary leading-snug mb-1 md:mb-1.5" data-id="element-101" data-mp="2bnvt4s9r0htk">
                  {bono.title}
                </h3>
                <p className="font-sans text-[12px] md:text-[13.5px] leading-[1.45] md:leading-[1.55] text-txt-body mb-2.5 md:mb-3" data-id="element-102" data-mp="32bupfb4ok1hc">
                  {bono.desc}
                </p>
                <div className="mt-auto pt-2 md:pt-2.5 border-t border-line flex items-center justify-between gap-2" data-id="element-103" data-mp="2tdssb6o2hszc">
                  <span className="font-sans font-normal text-[12px] md:text-[14px] line-through text-danger" data-id="element-104" data-mp="2ogam3o1fprv1">
                    {bono.value}
                  </span>
                  <span className="bg-success text-white font-sans font-bold uppercase tracking-[1px] text-[8.5px] md:text-[10px] px-2 py-0.5 rounded-full" data-id="element-105" data-mp="3h46c69wi0liu">
                    GRATIS
                  </span>
                </div>
              </div>
            </article>
          </FadeIn>)}
      </div>

      <FadeIn delay={0.4} className="text-center bg-bg-card p-5 md:p-8 rounded-[20px] border border-line max-w-2xl mx-auto" data-id="element-106" data-mp="8ntmp3uhvvzw">
        <p className="font-sans text-[14px] md:text-[16px] leading-[1.6] text-txt-body mb-2" data-id="element-107" data-mp="3u8q9g8jva2t">
          Valor total de los 5 Bonos
        </p>
        <p className="font-sans font-normal text-[16px] md:text-[18px] line-through text-danger mb-2" data-id="element-108" data-mp="g61e08nki7xq">
          USD $39
        </p>
        <p className="font-display font-bold text-[20px] md:text-[36px] leading-[1.15] mb-1 md:mb-2 text-[#22C55E] uppercase tracking-[0.5px]" data-id="element-109" data-mp="l42uqmxitv61">
          🎁 GRATIS — CON TU MEGAPACK
        </p>
        <p className="font-sans text-[13px] md:text-[16px] leading-[1.5] text-txt-muted" data-id="element-110" data-mp="u5kfwjpscn1l">
          Incluidos en tu Megapack, sin costo extra.
        </p>
      </FadeIn>
    </Section>;
};
export const UrgencyCounter = () => {
  const {
    hours,
    minutes,
    seconds,
    isUrgent
  } = useCountdown();
  const accent = isUrgent ? 'text-danger' : 'text-brandGold';
  const boxBorder = isUrgent ? 'border-danger' : 'border-brandGold';
  const boxShadow = isUrgent ? 'shadow-[0_0_25px_rgba(239,68,68,0.45)] animate-red-glow' : 'shadow-[0_0_25px_rgba(212,162,76,0.35)]';
  const units = [{
    value: hours,
    label: 'hs'
  }, {
    value: minutes,
    label: 'min'
  }, {
    value: seconds,
    label: 'seg'
  }];
  return <section className="bg-bg-base px-4 md:px-6 py-10 md:py-20 overflow-hidden" data-id="element-111" data-mp="2md7oi5ruqqwp">
      <div className="max-w-[1100px] mx-auto" data-id="element-112" data-mp="2r8n16ue4igrj">
        <FadeIn className="max-w-3xl mx-auto" data-id="element-113" data-mp="2vbjouziit32c">
          <div className={`rounded-[20px] border-2 p-6 md:p-10 text-center space-y-6 bg-bg-card max-w-lg mx-auto transition-colors ${isUrgent ? 'border-danger shadow-[0_0_50px_rgba(239,68,68,0.25)]' : 'border-brandGold shadow-[0_0_50px_rgba(212,162,76,0.18)]'}`} data-id="element-114" data-mp="2vdcwah0yjrbe">
            <h3 className={`font-sans font-bold uppercase tracking-[1.5px] text-[11px] ${accent}`} data-id="element-115" data-mp="3a064l2c27wu3">
              ⏰ PRECIO DE LANZAMIENTO — CIERRA EN:
            </h3>
            <div className={`flex justify-center gap-2.5 md:gap-4 ${isUrgent ? 'animate-red-text-pulse' : ''}`} data-id="element-116" data-mp="3f1lq0vi5g5d0">
              {units.map((unit, i) => <React.Fragment key={unit.label} data-id="element-117" data-mp="3nzpbjrt4x24t" data-mpi={i}>
                  {i > 0 && <div className={`font-display font-bold text-[34px] md:text-[46px] py-2 flex items-center ${accent}`} data-id="element-118" data-mp="3ny4he3aqfl7h">
                      :
                    </div>}
                  <div className={`bg-bg-card border-2 rounded-[14px] w-[72px] h-[80px] md:w-[90px] md:h-[96px] flex flex-col items-center justify-center transition-colors ${boxBorder} ${boxShadow}`} data-id="element-119" data-mp="17cpkm4kxztrn">
                    <div className={`font-display font-bold text-[34px] md:text-[46px] leading-none ${accent}`} data-id="element-120" data-mp="yf6uilsfyp8w">
                      {unit.value.toString().padStart(2, '0')}
                    </div>
                    <div className={`font-sans font-bold uppercase tracking-[1.5px] text-[9px] md:text-[11px] mt-1 ${accent}`} data-id="element-121" data-mp="1r2t88mvdva9h">
                      {unit.label}
                    </div>
                  </div>
                </React.Fragment>)}
            </div>

            <div className="flex items-center justify-center gap-2 font-sans font-bold text-base md:text-xl text-gold-bright leading-snug" data-id="element-122" data-mp="2esnmn8vcp6vu">
              <AlertTriangle size={20} data-id="element-123" data-mp="29ystletuvfk3" />
              <span data-id="element-124" data-mp="2swmbq0rahamn">Después sube el precio. No vuelve.</span>
            </div>

            <div className="p-4 rounded-[14px] border border-line bg-bg-alt" data-id="element-125" data-mp="2xw7jn0ofstbi">
              <div className="flex justify-between font-sans text-[13px] md:text-[15px] leading-[1.5] text-txt-body mb-3" data-id="element-126" data-mp="2xybl7byv5g8m">
                <span className="flex items-center gap-2" data-id="element-127" data-mp="2o65wqc7tlgy6">
                  <span className="w-2 h-2 rounded-full bg-brandGold animate-pulse" data-id="element-128" data-mp="2o4l26mli9y0b"></span>{' '}
                  Cupos adquiridos al precio de lanzamiento
                </span>
                <span className="text-brandGold font-bold" data-id="element-129" data-mp="2f6itrlzg0330">Quedan pocos</span>
              </div>
              <div className="w-full bg-line rounded-full h-2 mb-2 overflow-hidden" data-id="element-130" data-mp="216zvdkwfo63w">
                <div className="bg-brandGold h-full rounded-full" style={{
                width: '71%'
              }} data-id="element-131" data-mp="256s01miphs3z"></div>
              </div>
              <p className="font-sans text-[12px] md:text-[13px] text-txt-muted text-right" data-id="element-132" data-mp="3vhv5irn9nt8s">
                71% de los cupos ya tomados
              </p>
            </div>
          </div>
        </FadeIn>
      </div>
    </section>;
};
export const Pricing = () => {
  const includes = [{
    icon: '🎬',
    label: 'Curso completo — 15 Módulos en Video HD',
    detail: 'TODO lo que necesitas dominar en un solo lugar',
    price: 'USD $67'
  }, {
    icon: '📚',
    label: '4 Manuales Técnicos Completos',
    detail: '475+ páginas de técnica pura lista para consulta',
    price: 'USD $49'
  }, {
    icon: '🎁',
    label: '5 Bonos Exclusivos',
    detail: 'Cartas de color, fichas, kit de materiales — TODO imprimible',
    price: 'USD $39'
  }];
  return <Section className="bg-bg-alt !pt-6 !pb-8 md:!pt-12 md:!pb-14" id="pricing" data-id="element-133" data-mp="2gn51ee76atlw">
      <div className="max-w-lg mx-auto" data-id="element-134" data-mp="2bo1wquv3upeg">
        <FadeIn className="text-center mb-5 md:mb-7" data-id="element-135" data-mp="2bobmchc8mujm">
          <p className="font-sans text-[12.5px] md:text-[15px] text-txt-body" data-id="element-136" data-mp="m8m0uh66z06i">
            Estás a un paso de perfeccionarte con TODO organizado y aumentar tus precios...
          </p>
          <h2 className="font-display font-bold uppercase text-[26px] md:text-[40px] leading-[1.15] tracking-[0.5px] text-txt-primary mt-2 max-w-[18ch] md:max-w-none mx-auto" data-id="element-137" data-mp="jofszyegipko">
            OFERTA ESPECIAL DE{' '}
            <span className="text-brandGold" data-id="element-138" data-mp="spdrk164m4vv">LANZAMIENTO</span>
          </h2>
        </FadeIn>

        <FadeIn delay={0.1} data-id="element-139" data-mp="xnyqzydk0kj9">
          <div className="relative rounded-[20px] md:rounded-[24px] border-2 border-brandGold bg-bg-card px-4 pt-7 pb-4 md:px-7 md:pt-9 md:pb-6 shadow-[0_0_60px_rgba(212,162,76,0.18)]" data-id="element-140" data-mp="xn5t7mwkp5d9">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-brandGold text-[#14100A] font-sans font-bold uppercase tracking-[1.5px] text-[9.5px] md:text-[11px] px-3 py-1 rounded-full whitespace-nowrap shadow-[0_0_20px_rgba(212,162,76,0.45)]" data-id="element-141" data-mp="4yrt91lxbyz2">
              ⭐ MÁS ELEGIDO
            </div>

            <p className="font-sans font-bold uppercase tracking-[1.5px] text-[10px] md:text-[12px] text-txt-muted text-center mb-3 md:mb-4" data-id="element-142" data-mp="3dg4pn5y01jhd">
              Lo que incluye tu Megapack
            </p>

            <ul className="divide-y divide-line border-y border-line" data-id="element-143" data-mp="3av6rnrvfs3cn">
              {includes.map(item => <li key={item.label} className="flex items-center justify-between gap-3 py-2.5 md:py-3" data-id="element-144" data-mp="35vkchcw1sd17">
                  <div className="flex items-start gap-2 min-w-0" data-id="element-145" data-mp="30vxxaxwnsmpr">
                    <span className="text-[15px] md:text-lg leading-none mt-0.5" aria-hidden="true" data-id="element-146" data-mp="2vxo0yjz77g2k">
                      {item.icon}
                    </span>
                    <div className="min-w-0" data-id="element-147" data-mp="2rxdn6l55zld5">
                      <p className="font-sans font-bold text-[12.5px] md:text-[15px] text-txt-primary leading-snug" data-id="element-148" data-mp="2n5m2usclqbvg">
                        {item.label}
                      </p>
                      <p className="font-sans text-[10.5px] md:text-[12.5px] leading-[1.35] text-txt-muted" data-id="element-149" data-mp="2d76e2w11rep4">
                        {item.detail}
                      </p>
                    </div>
                  </div>
                  <span className="font-sans font-bold text-[12.5px] md:text-[15px] line-through text-danger shrink-0" data-id="element-150" data-mp="245ygyha9ojwm">
                    {item.price}
                  </span>
                </li>)}
            </ul>

            <div className="pt-3 md:pt-4 text-center" data-id="element-151" data-mp="1ykpr2kz0a4te">
              <p className="font-sans font-bold uppercase tracking-[1.5px] text-[10px] md:text-[12px] text-txt-muted" data-id="element-152" data-mp="1yi3cw2hm0r27">
                Valor original
              </p>
              <p className="font-display font-bold text-[20px] md:text-[26px] leading-none line-through text-danger mt-1" data-id="element-153" data-mp="1pkb5dki4qnpd">
                USD $155
              </p>
            </div>

            <div className="mt-3 md:mt-4 rounded-[16px] border border-brandGold/30 bg-bg-alt px-4 py-3.5 md:py-5 text-center" data-id="element-154" data-mp="2h9w7208f0ibi">
              <p className="font-sans font-bold uppercase tracking-[1.5px] text-[10px] md:text-[12px] text-brandGold" data-id="element-155" data-mp="23jam85zehyaz">
                Acceso completo hoy por
              </p>
              <p className="font-display font-bold text-[42px] md:text-[58px] leading-none text-brandGold mt-1" data-id="element-156" data-mp="2w7g6ysqgfnat">
                $15{' '}
                <span className="font-sans font-bold text-[14px] md:text-[18px] align-middle" data-id="element-157" data-mp="310rgx2qkck6y">
                  USD
                </span>
              </p>
              <p className="font-sans font-bold text-[11px] md:text-[13px] text-success mt-2" data-id="element-158" data-mp="36bde8w06bsd9">
                90% de descuento — Solo en lanzamiento
              </p>
              <p className="font-sans text-[10.5px] md:text-[13px] text-txt-muted mt-1.5" data-id="element-159" data-mp="3f3yi75z5bui1">
                Pago único · Sin suscripciones · Acceso inmediato
              </p>
            </div>

            <LocalCurrencyNote className="mt-3 md:mt-4" data-id="element-160" data-mp="2ndk9do5zp8d8" />

            <div className="mt-4 md:mt-5" data-id="element-161" data-mp="2nf3ovcjtnfdy">
              <CTAButton href={PRODUCTS.megapack.checkoutUrl} fullWidth trackingProduct="megapack" onClick={markCheckoutStarted} className="!py-3.5 md:!py-4 !text-[14px] md:!text-base" data-id="element-162" data-mp="31dla93m98fsp">
                🔥 QUIERO EL MEGAPACK COMPLETO
              </CTAButton>
              <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 mt-2.5 md:mt-3.5 font-sans text-[10px] md:text-[12.5px] text-txt-muted" data-id="element-163" data-mp="goxkw03bdj19">
                <span data-id="element-164" data-mp="264zsgcq8wd2e">🔒 Pago seguro</span>
                <span data-id="element-165" data-mp="26258snf22wa1">⚡ Acceso en minutos</span>
                <span data-id="element-166" data-mp="2a4s2ktvaer9v">🛡️ Garantía 7 días</span>
              </div>
            </div>
          </div>
        </FadeIn>
      </div>
    </Section>;
};